import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Route, Switch, Redirect, withRouter } from 'react-router-dom'
import { connect, useDispatch } from 'react-redux'
import track from 'react-tracking'
import { dispatch, digestUser } from '@ynap/analytics'
import { authActions } from '@ynap/auth-state'
import { countryActions } from '@ynap/country-state'
import Spinner from '@ynap/spinner'
import { getHeadConfig } from '@ynap/config-state'
import { OptimizelyProvider } from '@ynap/optimizely-utils'
import { WithReducedSiteFurniture } from '@ynap/with-reduced-site-furniture'
import ImmutablesProvider from '@ynap/immutables-provider'
import { MetaData } from '../../utils/getMetaData'

import {
    Basket,
    ErrorBoundaryCatchAll,
    LoginStep,
    ShippingAddressStep,
    ShippingOptionsStep,
    PaymentStep,
    AuthoriseStep,
    OrderConfirmation,
    PurchasePath,
    LineItemEditOverlay,
    PackagingOptionsStep,
} from 'app/components'

import {
    getCart,
    getLoginStep,
    getShippingAddressStep,
    getShippingOptionsStep,
    getPaymentStep,
    getAuthoriseStep,
    getOrderConfirmation,
    getProductDetails,
    getPackagingOptionsStep,
    getMultipleLanguageCountries,
    cartActions,
    getUberToken,
} from 'app/state/cart-state'
import getSpinner from 'app/state/checkout-state/selectors/getSpinner'
import pushDataToLiveChat from '../../utils/live-chat'
import { addToBagWithCartSync } from '../../state/cart-state/actions/addToCart'
import { fetchUserSummary } from 'app/state/cart-state/actions'

const SpinnerContainer = connect(getSpinner)(Spinner)
const BasketContainer = connect(
    (state, props) => ({
        ...getCart(state, props),
        addToBagState: state.addToCart,
        globalConfig: state.config.global,
        countries: state.countries,
        uberToken: getUberToken(state),
        crisisMessages: state.crisisMessages,
        jitMessages: state.jitMessages,
    }),
    {
        ...cartActions,
        ...countryActions,
        ...authActions,
        addToBagWithCartSync,
    }
)(Basket)

const LoginContainer = connect((state) => ({ ...getCart(state), ...getLoginStep(state), ...getMultipleLanguageCountries(state) }), {
    ...authActions,
    ...cartActions,
})(LoginStep)

const ShippingAddressContainer = connect(getShippingAddressStep, { ...cartActions, ...countryActions })(ShippingAddressStep)
const ShippingOptionsContainer = connect(getShippingOptionsStep, cartActions)(ShippingOptionsStep)
const PackagingOptionsContainer = connect(getPackagingOptionsStep, cartActions)(PackagingOptionsStep)

const PaymentContainer = connect(getPaymentStep, { ...cartActions, ...countryActions })(PaymentStep)
const AuthoriseContainer = connect(getAuthoriseStep, cartActions)(AuthoriseStep)
const OrderConfirmationContainer = connect((state) => ({ ...getOrderConfirmation(state), ...getMultipleLanguageCountries(state) }), {
    ...authActions,
    ...cartActions,
    ...countryActions,
})(OrderConfirmation)
const PurchasePathContainer = connect(
    (state, props) => ({ ...getCart(state, props), ...getMultipleLanguageCountries(state), uberToken: getUberToken(state) }),
    { ...cartActions, ...countryActions }
)(PurchasePath)
const LineItemEditOverlayContainer = connect(getProductDetails, {
    ...cartActions,
    addToBagWithCartSync,
})(LineItemEditOverlay)
const WithReducedSiteFurnitureContainer = connect(null, { setSidebar: cartActions.setSidebar })(WithReducedSiteFurniture)

const propTypes = {
    messages: PropTypes.object.isRequired,
    locale: PropTypes.shape({
        id: PropTypes.string,
    }),
}

export function App({ messages, locale, flags, isBare, links, sizeGridFeatureToggle, config, appState, optimizely, ssr = false, nativeapp }) {
    const dispatch = useDispatch()
    const [canMakeApplePayments, setCanMakeApplePayments] = useState(false)
    const { userSummary } = appState

    useEffect(() => {
        window.ApplePaySession &&
            window.ApplePaySession.canMakePayments() &&
            window.ApplePaySession.supportsVersion(config?.head?.applepay?.version || 8) &&
            setCanMakeApplePayments(true)
    }, [])

    useEffect(() => {
        dispatch(fetchUserSummary())
    }, [])

    const { enablePackagingStep, enableConsidered } = flags

    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const paymentAuthoriseUrl = `${origin}/${locale.id}/checkout/authorise`.toLowerCase()

    const renderPurchasePath = ({ location, match, history, isSkeleton = false, isLoginSkeleton = false }) => {
        const purchasePath = (
            <PurchasePathContainer
                isSkeleton={isSkeleton}
                isLoginSkeleton={isLoginSkeleton}
                messages={messages}
                location={location}
                match={match}
                locale={locale}
                history={history}
                nativeapp={nativeapp}
            >
                <ShippingAddressContainer
                    path="/:locale/checkout/shipping-address"
                    optionsPath="/:locale/checkout/shipping-options"
                    packagingPath="/:locale/checkout/packaging-options"
                    paymentPath="/:locale/checkout/payment-options"
                    messages={messages}
                    locale={locale}
                />
                <ShippingOptionsContainer
                    path="/:locale/checkout/shipping-options"
                    paymentPath="/:locale/checkout/payment-options"
                    messages={messages}
                    locale={locale}
                    enableConsidered={enableConsidered}
                />
                {enablePackagingStep && <PackagingOptionsContainer path="/:locale/checkout/packaging-options" messages={messages} locale={locale} />}
                <PaymentContainer
                    path="/:locale/checkout/payment-options"
                    messages={messages}
                    locale={locale}
                    returnUrl={paymentAuthoriseUrl}
                    history={history}
                    canMakeApplePayments={canMakeApplePayments}
                    {...{ nextPath: `/${locale.id}/checkout/confirmation` }}
                    optimizely={optimizely}
                    userSummary={userSummary?.user}
                />
            </PurchasePathContainer>     
        )
        return isLoginSkeleton ? (
            purchasePath
        ) : (
            <WithReducedSiteFurnitureContainer key="purchase-path-reduced-sf-wrapper">{purchasePath}</WithReducedSiteFurnitureContainer>
        )
    }

    return (
        <ErrorBoundaryCatchAll messages={messages}>
            <OptimizelyProvider optimizely={optimizely} isServerSide={ssr} pageGroup="checkout" nativeapp={nativeapp}>
                <ImmutablesProvider value={{ messages, config, appState }}>
                    <SpinnerContainer>
                        <Switch>
                            <Route
                                path="/:locale/checkout"
                                exact
                                render={({ location, history }) => (
                                    <BasketContainer
                                        messages={messages}
                                        location={location}
                                        showContinueShoppingLink={!isBare}
                                        history={history}
                                        continueShoppingPath={links.continueShoppingPath}
                                        sizeGridFeatureToggle={sizeGridFeatureToggle}
                                        LineItemEditOverlayContainer={LineItemEditOverlayContainer}
                                        userSummary={userSummary?.user}
                                    />
                                )}
                            />

                            <Route
                                path="/:locale/checkout/login"
                                render={({ location, match, history }) => (
                                    <WithReducedSiteFurnitureContainer key="login-path-reduced-sf-wrapper">
                                        <MetaData title={messages.loginStep.title()} brandId={config.global.application.brandId} />
                                        <LoginContainer
                                            messages={messages}
                                            locale={locale}
                                            redirect={`/${match.params.locale}/checkout/payment-options`}
                                            history={history}
                                            placeholder={() => renderPurchasePath({ location, match, isSkeleton: true, isLoginSkeleton: true })}
                                            returnUrl={`${typeof window !== 'undefined' ? window.location.origin : ''}/${
                                                match.params.locale
                                            }/checkout/authorise`}
                                            config={config}
                                        />
                                    </WithReducedSiteFurnitureContainer>
                                )}
                            />
                            <Route
                                path="/:locale/checkout/authorise"
                                render={({ location, match, history }) => (
                                    <WithReducedSiteFurnitureContainer key="authorise-path-reduced-sf-wrapper">
                                        <MetaData title={'Checkout'} brandId={config.global.application.brandId} />
                                        <AuthoriseContainer
                                            messages={messages}
                                            locale={locale}
                                            redirect={`/${match.params.locale}/checkout/confirmation`}
                                            cancelRedirect={`/${match.params.locale}/checkout/payment-options`}
                                            location={location}
                                            history={history}
                                        />
                                    </WithReducedSiteFurnitureContainer>
                                )}
                            />
                            <Route
                                path="/:locale/checkout/confirmation"
                                render={(props) => (
                                    <>
                                        <MetaData title={'Checkout'} brandId={config.global.application.brandId} />
                                        <OrderConfirmationContainer
                                            messages={messages}
                                            locale={locale}
                                            continueShoppingPath={links.continueShoppingPath}
                                            enableConsidered={enableConsidered}
                                            userSummary={userSummary?.user}
                                            config={config}
                                            {...props}
                                        />
                                    </>
                                )}
                            />
                            <Route path="/:locale/checkout" render={renderPurchasePath} />
                            <Route render={({ location }) => <Redirect to={location.pathname} />} />
                        </Switch>
                    </SpinnerContainer>
                </ImmutablesProvider>
            </OptimizelyProvider>
        </ErrorBoundaryCatchAll>
    )
}

App.propTypes = propTypes

export default withRouter(
    // TODO: TONWEB-2953 - Remove paymentSystem exposure once TON has migrated to GPS
    // optimizely prop should override optimizely value coming from config
    connect((state, props) => ({ ...getHeadConfig(state), config: state.config, optimizely: props.optimizely, appState: state }))(
        track(
            ({ config, appState }) => ({
                category: {
                    primaryCategory: 'checkout',
                },
                lpTag: { customSection: config.head.liveChatCustomSection },
                guestUserEmail: appState?.cart?.x_selfEmailAddress,
            }),
            {
                dispatch: ({ lpTag, guestUserEmail, ...gtmData } = {}) => {
                    dispatch(gtmData, {}, digestUser(guestUserEmail))
                    pushDataToLiveChat(gtmData, lpTag)
                },
            }
        )(App)
    )
)
